import "./locationCriticalDetailsAddComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import CommonService from "../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import {useSearchParams} from "react-router-dom";

interface LocationCriticalDetailsAddComponentProps {
    locationId: number;
    next: Function;
    cancel: Function;
    prev: Function;
    locationDetails: any;
}

const LocationCriticalDetailsValidationSchema = Yup.object({
    emailId: Yup.string()
        .email("Invalid Email ID")
        .nullable()
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    mobileNumber: Yup.string()
        .nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    faxNumber: Yup.string().nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
});

interface LocationCriticalDetailsFormSchema {
    mobileNumber: string;
    emailId: string;
    faxNumber: string;
}

const formInitialValues: LocationCriticalDetailsFormSchema = {
    mobileNumber: "",
    emailId: "",
    faxNumber: "",
}

const LocationCriticalDetailsAddComponent = (props: LocationCriticalDetailsAddComponentProps) => {

    const {locationId, next, cancel, locationDetails, prev} = props;

    const [initialValues, setInitialValues] = useState<LocationCriticalDetailsFormSchema>(formInitialValues);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        const criticalDetails = {...values};
        criticalDetails.mobileNumber = criticalDetails.mobileNumber.replace(/[^0-9]/g, "");
        criticalDetails.faxNumber = criticalDetails?.faxNumber.replace(/[^0-9]/g, "");
        const payload = {criticalDetails: criticalDetails,lastCompletedStep:lastCompletedStep};
        setSubmitting(true);
        CommonService._location.addCriticalDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                next(response.data.criticalDetails);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [locationId, next,lastCompletedStep]);


    useEffect(() => {
        if (locationDetails?.criticalDetails) {
            setInitialValues(locationDetails?.criticalDetails);
        }
    }, [locationDetails]);


    return (
        <div className="location-critical-details-add screen t-form-screen">
            <FormTitleWithNavigationBack
                title={'Critical Details'}
                tooltipData={"These details must be given with better certainty, as the patient will access these in the event of an emergency or for queries."}
                handleClick={prev}
            />

            <div className="t-form  t-form-back-navigation">
                <Formik
                    validationSchema={LocationCriticalDetailsValidationSchema}
                    initialValues={initialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, values,dirty, isValid, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"location-critical-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'mobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'After Hours Contact Number'}
                                                            placeholder={'After Hours Contact Number'}
                                                            maxLength={14}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'emailId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'faxNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Fax Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>

                                        </Grid>
                                    </Grid>
                                    <FeedbackComponent className={"mrg-top-20"}>
                                        Note: This information will be used in the patient’s result report. The contact
                                        provided must either be the clinical decision maker, or have 24/7 access to that
                                        person to prevent potential delay in patient care.
                                    </FeedbackComponent>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>

                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        {dirty ?  "Next":"Skip"}
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LocationCriticalDetailsAddComponent;
